import React, { useMemo, useState } from 'react';
import { FilterGroup } from 'src/components/BeaconV2Components/common/AppFilterButton/AppFilterModal';
import { useAddProductsModalFilters } from './AddProductsModalContext';
import BusinessUnitFilterGroup from 'src/components/BeaconV2Components/common/AppFilterButton/BusinessUnitFilterSection';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector } from 'src/utils/Hooks';
import { useMainEntityId } from 'src/utils/Hooks/reduxSelectors';

export const AddProductFilters = () => {
  const mainEntity = useAppSelector((state) => state.entityService.mainEntity);
  const mainEntityId = useMainEntityId();

  const { filters, setCategories, setSubcategories, setBrands, setSegments, setSelectedBusinessUnitIds } =
    useAddProductsModalFilters();
  const segmentsState = useAppSelector((state) => state.segments);
  const { savedSearchesById } = segmentsState;

  const categoryOptions = useMemo(() => {
    return filters.categories;
  }, [filters.categories]);

  const onSelectCategory = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedCategoryId = String(e.target.value);
    const newCategories = categoryOptions.map((category) => {
      return {
        ...category,
        isSelected: selectedCategoryId === String(category.id) ? !category.isSelected : category.isSelected
      };
    });

    setCategories(newCategories);
    const selectedCategories = newCategories.filter(({ isSelected }) => isSelected);
    if (selectedCategories.length < 1) {
      setSubcategories(filters.subcategories.map((subcategory) => ({ ...subcategory, isSelected: false })));
    } else {
      // Clean up subcategories
      const validSubcategoriesById = selectedCategories.flatMap(({ childEntities }) =>
        childEntities.map(({ id }) => String(id))
      );
      const validSubcategories = filters.subcategories.map((subcategory) => {
        return {
          ...subcategory,
          isSelected: validSubcategoriesById.includes(String(subcategory.id)) ? subcategory.isSelected : false
        };
      });
      setSubcategories(validSubcategories);
    }
  };

  const subCategoryOptions = useMemo(() => {
    let selectedCategories = categoryOptions.filter(({ isSelected }) => isSelected);

    if (mainEntity.type === 'category' && String(mainEntity.id) !== '0') {
      selectedCategories = categoryOptions.filter(({ id }) => String(id) === String(mainEntity.id));
    }

    if (selectedCategories.length > 0) {
      const availableSubcategoriesById = selectedCategories.flatMap(({ childEntities }) =>
        childEntities.map(({ id }) => String(id))
      );

      return filters.subcategories
        .filter(({ id }) => availableSubcategoriesById.includes(String(id)))
        .map((subcategory) => {
          return {
            ...subcategory,
            isSelected: subcategory.isSelected
          };
        });
    } else {
      return filters.subcategories.map((subcategory) => ({
        ...subcategory,
        isSelected: subcategory.isSelected
      }));
    }
  }, [filters.subcategories, categoryOptions, mainEntity]);

  const onSelectSubcategory = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedSubcategoryId = String(e.target.value);
    const newSubcategories = filters.subcategories.map((subcategory) => {
      return {
        ...subcategory,
        isSelected: selectedSubcategoryId === String(subcategory.id) ? !subcategory.isSelected : subcategory.isSelected
      };
    });
    setSubcategories(newSubcategories);
  };

  const brandOptions = useMemo(() => filters.brands, [filters.brands]);

  const onSelectBrand = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedBrandId = String(e.target.value);
    const newBrands = brandOptions.map((brand) => ({
      ...brand,
      isSelected: selectedBrandId === String(brand.id) ? !brand.isSelected : brand.isSelected
    }));
    setBrands(newBrands);
  };

  // Segments (single-select)
  const [availableSegments, setAvailableSegments] = useState(
    (() => {
      const { mySegments, teamSegments } = segmentsState;
      const combined = [...mySegments, ...teamSegments].sort((a, b) => {
        const aName = a.displayName || '';
        const bName = b.displayName || '';
        return aName.localeCompare(bName);
      });
      return combined.map((segment) => ({
        id: String(segment.id),
        displayName: segment.displayName,
        isSelected: false
      }));
    })()
  );

  const segmentOptions = useMemo(() => availableSegments, [availableSegments]);

  const onSelectSegment = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedId = String(e.target.value);
    const newSegments = segmentOptions.map((seg) => ({
      ...seg,
      isSelected: selectedId === seg.id
    }));
    setAvailableSegments(newSegments);
    setSegments(newSegments);
  };

  // Business Units
  const [selectedBusinessUnitFieldOption, setSelectedBusinessFieldOption] = useState<Set<string>>(new Set());

  const onBusinessUnitSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedBusinessUnitId = String(e.target.value);
    const newSet = new Set(selectedBusinessUnitFieldOption);
    if (newSet.has(selectedBusinessUnitId)) {
      newSet.delete(selectedBusinessUnitId);
    } else {
      newSet.add(selectedBusinessUnitId);
    }
    setSelectedBusinessFieldOption(newSet);
    // Reflect in context as flat array for override filters
    setSelectedBusinessUnitIds(Array.from(newSet));
  };

  // Compute whether there are any valid business units to display
  const hasDisplayableBusinessUnits = useMemo(() => {
    if (!segmentsState.businessUnits || segmentsState.businessUnits.length === 0) {
      return false;
    }
    
    return segmentsState.businessUnits
      .filter((bu) => bu.isTopLevel && bu.children?.length > 0 && bu.displayName)
      .some((topLevelBusinessUnit) => {
        const childLevelBusinessUnitOptions = topLevelBusinessUnit.children
          .map((childBusinessUnit: string) => {
            const childEntity = savedSearchesById.get(childBusinessUnit);
            return childEntity && childEntity.id && childEntity.displayName ? childEntity : null;
          })
          .filter((child) => child !== null);
        
        return childLevelBusinessUnitOptions.length > 0;
      });
  }, [segmentsState.businessUnits, savedSearchesById]);

  /**
   * Determines whether the filter should be shown for a given entity type.
   */
  const shouldShowFilter = (entityType: string) => {
    const currentEntityType = mainEntity.type;

    if (
      entityType === 'category' &&
      ['category', 'subcategory'].includes(currentEntityType) &&
      String(mainEntityId) !== '0'
    ) {
      return false;
    }

    if (entityType === 'subcategory' && currentEntityType === 'subcategory') {
      return false;
    }

    return true;
  };

  return (
    <Flex
      flexDirection="column"
      sx={{
        marginBottom: '24px',
        marginTop: '-24px'
      }}
    >
      <FilterGroup
        filterTitle="Brands"
        options={brandOptions}
        onSelect={onSelectBrand}
        placeholderText="Search brands"
      />

      {segmentsState.mySegments && segmentsState.mySegments.length > 0 && (
        <FilterGroup
          filterTitle="Segments"
          options={segmentOptions as any}
          onSelect={onSelectSegment}
          isRadio
          placeholderText="Search segments"
        />
      )}

      {hasDisplayableBusinessUnits && (
        <Text variant="subtitle1" sx={{ marginTop: '24px' }}>
          Business Units
        </Text>
      )}
      {hasDisplayableBusinessUnits &&
        segmentsState.businessUnits
          .filter((bu) => bu.isTopLevel && bu.children?.length > 0 && bu.displayName)
          .sort((a, b) => a.displayName.localeCompare(b.displayName))
          .map((topLevelBusinessUnit) => {
            const childLevelBusinessUnitOptions = topLevelBusinessUnit.children
              .map((childBusinessUnit: string) => {
                const childEntity = savedSearchesById.get(childBusinessUnit);
                return childEntity
                  ? { ...childEntity, isSelected: selectedBusinessUnitFieldOption.has(childBusinessUnit) }
                  : null;
              })
              .filter((child) => child && child.id && child.displayName) as Array<{
              id: string;
              displayName: string;
              isSelected: boolean;
            }>;

            if (childLevelBusinessUnitOptions.length === 0) return null;

            return (
              <BusinessUnitFilterGroup
                key={`bu-${topLevelBusinessUnit.id}`}
                filterTitle={topLevelBusinessUnit.displayName}
                options={childLevelBusinessUnitOptions}
                onSelect={onBusinessUnitSelect}
                isRadio={false}
                placeholderText="Search business units"
                selectedBusinessUnitFieldOption={selectedBusinessUnitFieldOption}
              />
            );
          })}

      {shouldShowFilter('category') ? (
        <FilterGroup
          filterTitle="Categories"
          options={categoryOptions}
          onSelect={onSelectCategory}
          placeholderText="Search categories"
        />
      ) : null}

      {shouldShowFilter('subcategory') ? (
        <FilterGroup
          filterTitle="Subcategories"
          options={subCategoryOptions}
          onSelect={onSelectSubcategory}
          placeholderText="Search subcategories"
        />
      ) : null}
    </Flex>
  );
};
